import React from 'react';
import { Clock, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { NotificationItemProps } from '@/types/notifications-types';
import { formatNotificationDate } from '@/utils/notification-utils';
import NotificationIcon from './NotificationIcon';

/**
 * Component to display a single notification
 */
const NotificationItem: React.FC<NotificationItemProps> = ({ 
  notification, 
  onRemoveNotification,
  onMarkAsRead // This prop is now optional
}) => {
  const { id, title, message, type, date, actionLabel, link, read } = notification;
  const navigate = useNavigate();
  
  // Get background color based on notification type
  const getBgColor = () => {
    switch (type) {
      case 'info':
        return 'bg-blue-50/80 dark:bg-blue-900/20 border-l-4 border-blue-500';
      case 'success':
        return 'bg-green-50/80 dark:bg-green-900/20 border-l-4 border-green-500';
      case 'warning':
        return 'bg-amber-50/80 dark:bg-amber-900/20 border-l-4 border-amber-500';
      case 'error':
        return 'bg-red-50/80 dark:bg-red-900/20 border-l-4 border-red-500';
      default:
        return 'bg-blue-50/80 dark:bg-blue-900/20 border-l-4 border-blue-500';
    }
  };

  // Handle click on the notification
  const handleClick = () => {
    // Mark as read if handler exists and notification isn't read yet
    if (onMarkAsRead && read === false) {
      onMarkAsRead(id);
    }
    
    // Handle navigation if there's a link
    if (link) {
      navigate(link);
    }
  };

  // Handle action button click
  const handleActionClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (link) {
      navigate(link);
    }
  };

  return (
    <div
      className={`
        ${getBgColor()}
        rounded-xl shadow-sm
        backdrop-blur-md
        hover:shadow-md
        ${link ? 'cursor-pointer' : 'cursor-default'}
        overflow-hidden
      `}
      onClick={link ? handleClick : undefined}
      data-test-id={`notification-${id}`}
    >
      <div className="flex items-start p-5">
        <div className="flex-shrink-0 mr-4 mt-1">
          <div className="p-2.5 rounded-full bg-white/70 dark:bg-slate-800/70 shadow-sm">
            <NotificationIcon type={type} />
          </div>
        </div>
        
        <div className="flex-1">
          <div className="flex justify-between items-start mb-1">
            <h3 className="font-semibold text-lg text-slate-800 dark:text-slate-200">
              {title}
              {read === false && (
                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-300">
                  Unread
                </span>
              )}
            </h3>
            <div className="flex items-center gap-2">
              {onMarkAsRead && read === false && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onMarkAsRead(id);
                  }}
                  className="h-7 w-7 rounded-full flex items-center justify-center bg-blue-100 hover:bg-blue-200 dark:bg-blue-900/30 dark:hover:bg-blue-800/40 text-blue-600 dark:text-blue-400"
                  title="Mark as read"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </button>
              )}
              {onRemoveNotification && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemoveNotification(id);
                  }}
                  className="h-7 w-7 rounded-full flex items-center justify-center bg-red-100 hover:bg-red-200 dark:bg-red-900/30 dark:hover:bg-red-800/40 text-red-600 dark:text-red-400"
                  title="Delete notification"
                >
                  <X size={16} />
                </button>
              )}
            </div>
          </div>
          
          <p className="text-slate-600 dark:text-slate-300 mb-3 leading-relaxed">
            {message}
          </p>
          
          <div className="flex items-center text-xs text-slate-500 dark:text-slate-400">
            <Clock size={14} className="mr-1" />
            <time dateTime={date}>{formatNotificationDate(date)}</time>
          </div>
          
          {actionLabel && link && (
            <div className="mt-4">
              <button
                onClick={handleActionClick}
                className={`
                  ${type === 'success' ? 'bg-green-500 hover:bg-green-600' : 
                    type === 'warning' ? 'bg-amber-500 hover:bg-amber-600' : 
                    type === 'error' ? 'bg-red-500 hover:bg-red-600' : 
                    'bg-blue-500 hover:bg-blue-600'}
                  text-white px-4 py-2 rounded-lg text-sm font-medium
                `}
              >
                {actionLabel}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationItem;
